/** The wizard's frame: header, step rail, body, footer. No step logic and no service calls —
 *  the container above it owns the reducer and every write.
 *
 *  One `<RTL>` lives at the page root, never here and never inside a step: each `CacheRtl`
 *  provider owns its own emotion insertion point, and nesting them reproduces the
 *  `NotFoundError: insertBefore` failure described in `WizardField.tsx`. */

import type { ReactNode } from "react";
import { Box, Button, IconButton, LinearProgress, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Close as CloseIcon } from "@mui/icons-material";
import { t } from "../../dashboard/coach/coachTokens";
import StepRail from "./StepRail";
import { STEP_LABELS, type StepId, type WizardState } from "./wizardReducer";

const footerButtonSx = {
  fontSize: "13px",
  fontWeight: 600,
  padding: "8px 22px",
  borderRadius: t.rs,
  textTransform: "none" as const,
  boxShadow: "none",
};

interface WizardShellProps {
  title: string;
  subtitle?: string;
  /** Every step, for the rail and the "שלב N מתוך M" counter. */
  steps: StepId[];
  /** The subset the rail may jump to. */
  navigable: StepId[];
  /** Master switch for the rail — off while there is nothing yet to navigate between. */
  canNavigate: boolean;
  state: WizardState;
  children: ReactNode;
  /** Label for the primary action — "המשך" mid-flow, something final on the last step. */
  primaryLabel: string;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  /** Absent on a mandatory step, which is how the footer knows not to offer a skip. */
  onSkip?: () => void;
  onBack?: () => void;
  onPrimary: () => void;
  onExit: () => void;
  onSelectStep: (step: StepId) => void;
}

const WizardShell = ({
  title,
  subtitle,
  steps,
  navigable,
  canNavigate,
  state,
  children,
  primaryLabel,
  primaryDisabled,
  primaryLoading,
  onSkip,
  onBack,
  onPrimary,
  onExit,
  onSelectStep,
}: WizardShellProps) => {
  const position = steps.indexOf(state.current) + 1;

  return (
    <Box sx={{ backgroundColor: t.bg, minHeight: "100%", padding: { xs: "12px", md: "28px" } }}>
      <Box
        sx={{
          maxWidth: "1100px",
          margin: "0 auto",
          backgroundColor: t.white,
          border: `1px solid ${t.border}`,
          borderRadius: t.r,
          boxShadow: t.sh,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: { xs: "14px 16px", md: "18px 24px" },
            borderBottom: `1px solid ${t.border}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "17px", fontWeight: 700, color: t.text }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: "12.5px", color: t.t2, marginTop: "2px" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onExit}
            size="small"
            aria-label="סגור"
            sx={{ color: t.t3, "&:hover": { backgroundColor: t.bg, color: t.text } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* The rail does not fit a phone, so below md the position becomes one line + a bar. */}
        <Box sx={{ display: { xs: "block", md: "none" }, padding: "12px 16px 0" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: t.t2 }}>
            {`שלב ${position} מתוך ${steps.length} · ${STEP_LABELS[state.current]}`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(position / steps.length) * 100}
            sx={{
              marginTop: "8px",
              height: "4px",
              borderRadius: "999px",
              backgroundColor: t.borderLight,
              "& .MuiLinearProgress-bar": { backgroundColor: t.green, borderRadius: "999px" },
            }}
          />
        </Box>

        {/* Body: rail beside the step. `row` puts the rail at the start of the reading
            direction, which stylis flips to the right-hand side in Hebrew. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            gap: { md: "28px" },
          }}
        >
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              padding: "22px 20px",
              borderInlineEnd: `1px solid ${t.borderLight}`,
              backgroundColor: t.white,
            }}
          >
            <StepRail
              steps={steps}
              navigable={navigable}
              current={state.current}
              stepStates={state.steps}
              canNavigate={canNavigate}
              onSelect={onSelectStep}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, padding: { xs: "18px 16px", md: "24px 24px 28px 0" } }}>
            {children}
          </Box>
        </Box>

        {/* Footer. `space-between` with cancel at the start matches the client dialogs. */}
        <Box
          sx={{
            display: "flex",
            flexWrap: { xs: "wrap", md: "nowrap" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
            padding: { xs: "14px 16px", md: "16px 24px" },
            borderTop: `1px solid ${t.border}`,
            backgroundColor: t.bg,
          }}
        >
          <Button
            onClick={onExit}
            variant="outlined"
            sx={{
              ...footerButtonSx,
              borderColor: t.border,
              color: t.t2,
              "&:hover": { borderColor: t.t2, backgroundColor: t.white },
            }}
          >
            ביטול
          </Button>

          <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {onBack && (
              <Button
                onClick={onBack}
                variant="outlined"
                sx={{
                  ...footerButtonSx,
                  borderColor: t.border,
                  color: t.t2,
                  "&:hover": { borderColor: t.t2, backgroundColor: t.white },
                }}
              >
                חזור
              </Button>
            )}
            {onSkip && (
              <Button
                onClick={onSkip}
                sx={{ ...footerButtonSx, color: t.t2, "&:hover": { backgroundColor: t.white } }}
              >
                דלג על השלב
              </Button>
            )}
            <LoadingButton
              onClick={onPrimary}
              loading={primaryLoading}
              disabled={primaryDisabled}
              variant="contained"
              sx={{
                ...footerButtonSx,
                backgroundColor: t.green,
                color: t.white,
                "&:hover": { backgroundColor: t.greenDark, boxShadow: "none" },
                "&.Mui-disabled": { backgroundColor: t.border, color: t.t3 },
              }}
            >
              {primaryLabel}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WizardShell;
